import { useEffect, useRef, useState } from 'react';
import { getBulkRefreshStatus, startBulkRefresh } from '../api';
import { BulkRefreshStatus } from '../types';

const POLL_INTERVAL_MS = 2000;

interface UseBulkRefreshReturn {
  isRefreshing: boolean;
  bulkStatus: BulkRefreshStatus | null;
  triggerRefresh: () => Promise<void>;
}

export function useBulkRefresh(onComplete?: () => void): UseBulkRefreshReturn {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<BulkRefreshStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const status = await getBulkRefreshStatus();
        setBulkStatus(status);
        if (!status.is_running) {
          stopPolling();
          setIsRefreshing(false);
          onComplete?.();
        }
      } catch {
        stopPolling();
        setIsRefreshing(false);
      }
    }, POLL_INTERVAL_MS);
  };

  useEffect(() => () => stopPolling(), []);

  const triggerRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const status = await startBulkRefresh();
      setBulkStatus(status);
      startPolling();
    } catch {
      setIsRefreshing(false);
    }
  };

  return { isRefreshing, bulkStatus, triggerRefresh };
}